import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import { endOfMonth, format, startOfMonth, sub } from "date-fns";
import { getUserToken, logout } from "../auth-provider";
import { GoogleProperties, GoogleProperty } from "../domain/types";
import { downloadAuthorizedFile, downloadFilename, type AuthorizedDownload } from "./download-helper";
import { exactUserUrl, inspectionUrlsForApi } from "./inspection-urls";
import { urlHasBlankPathSegment } from "./project-id";
import { accountPropertiesFromResponse, contentCheckPageFilter } from "./query-property";
import {
  moduleReportCountBody,
  parseModuleReportCounts,
  ReportModule,
} from "./module-report-counts";
import {
  Account,
  AdhocExport,
  APISchedule,
  BrandQueryExamplesResponse,
  Contentcheck,
  ContentDecay,
  Dimension,
  GoogleAuthentication,
  GoogleSearchIncidentsResponse,
  OptimizationWizard,
  Project,
  RegexPresetInput,
  TrafficInsight,
  TrafficInsightRowsPage,
  PerformanceDetailsReport,
  QueryCoverage,
  RankTrackingReport,
  RankTrackingSuggestion,
  RankDashboard,
  ContentRankMonitorRef,
  TrafficTrendPageFilter,
  TrafficTrendReport,
  TrafficTrendSummary,
  URLInspectionBatch,
  URLInspectionChange,
  URLInspectionURLHistory,
  MonitoredURL,
  WaitingURL,
  URLInspectionHistory,
  URLInspectionResultOptions,
  URLInspectionResultPage,
  URLInspectionQueue,
  URLInspectionReject,
  URLInspectionSettings,
  URLInspectionSummary,
  Annotation,
  AnnotationGroup,
  AnnotationList,
  AnnotationPackage,
  AnnotationWrite,
  NotificationAccountSample,
  NotificationPreferences,
  NotificationProjectOption,
  NotificationTemplate,
} from "./api/types";

// In development the dev server proxies these paths (src/setupProxy.js), so the
// browser stays on localhost and the staging API CORS allowlist does not apply.
function propertyField(propertySiteUrl?: string): { property_site_url?: string } {
  const value = (propertySiteUrl || "").trim();
  if (value === "") {
    return {};
  }
  return { property_site_url: value };
}

const apiRoot =
  process.env.NODE_ENV === "development" ? "" : process.env.REACT_APP_API_HOST;

export type DashboardGlobalChartPoint = {
  metric_date: string;
  clicks: number;
  impressions: number;
};

export type DashboardGlobalChart = {
  project_id: string;
  points: DashboardGlobalChartPoint[];
};

export type DashboardVisibilityPoint = {
  date: string;
  value: number;
};

export type DashboardVisibilitySeries = {
  project_id: string;
  points: DashboardVisibilityPoint[];
};

export class ApiClient {
  private axios: AxiosInstance;
  constructor(passedAxios?: AxiosInstance) {
    if (passedAxios !== undefined) {
      this.axios = passedAxios;
    } else {
      this.axios = axios.create({
        baseURL: `${apiRoot}/api/v1`,
      });
    }
    this.axios.interceptors.request.use(this.handleRequest);
    this.axios.interceptors.response.use((response: any) => {
      return response;
    }, this.handleError);
  }

  private handleError(error: AxiosError) {
    const config = error.config as (AxiosRequestConfig & { skipAuthRedirect?: boolean }) | undefined;
    if (error.response?.status === 401 && !config?.skipAuthRedirect) {
      logout();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
  private handleRequest(config: InternalAxiosRequestConfig) {
    // A blank segment is the %20 project id. Do not send it: the API can
    // answer as if the account had no project, and a refresh stays on that URL.
    if (urlHasBlankPathSegment(config.url || "")) {
      throw new Error("Refusing a request with a blank project id.");
    }
    const token = `Bearer ${getUserToken()}`;
    // Axios 1 passes AxiosHeaders, which only keeps values written with set.
    // Unit tests still hand the interceptor a plain object.
    if (typeof config.headers.set === "function") {
      config.headers.set("Authorization", token);
    } else {
      (config.headers as unknown as Record<string, string>)["Authorization"] = token;
    }
    return config;
  }

  public async updateSearchVolume(value: number, id: any) {
    return this.axios
      .post("/keyword/update-search-volume", {
        value,
        id,
      })
      .then((res) => res.data)
      .catch((error) => error);
  }

  public async updateKeywordMapSearchVolume(params: { projectId: string }) {
    return this.axios
      .post(`/keyword-map/${params.projectId}/update-search-volume`)
      .then((res) => res.data)
      .catch((error) => error);
  }

  public async login(login: string, password: string) {
    return this.axios.post("/auth/login", {
      email: login,
      password,
    });
  }

  public async refreshSession() {
    return this.axios.get("/auth/session");
  }

  public async register(body: {
    name: string;
    email: string;
    password: string;
    plan: string;
    privacy_policy_version: string;
    privacy_consent: boolean;
    company_website?: string;
    salutation?: string;
    company?: string;
    street?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    vat_id?: string;
    billing_email?: string;
    project_count?: number;
    message?: string;
  }) {
    return this.axios.post("/auth/register", body);
  }

  public async confirmEmail(token: string) {
    return this.axios.post("/auth/confirm-email", { token });
  }

  public async resendConfirmation(email: string, companyWebsite?: string) {
    return this.axios.post("/auth/resend-confirmation", {
      email,
      company_website: companyWebsite || "",
    });
  }

  public async submitBillingRequest(body: {
    salutation: string;
    company: string;
    street: string;
    postal_code: string;
    city: string;
    country: string;
    vat_id?: string;
    billing_email: string;
    project_count: number;
    message?: string;
  }) {
    return this.axios.post("/billing-requests", body);
  }

  public async getBillingProfile() {
    return this.axios
      .get<{
        billing: {
          salutation: string;
          company: string;
          street: string;
          postal_code: string;
          city: string;
          country: string;
          vat_id: string;
          billing_email: string;
          project_count: number;
          message: string;
        } | null;
      }>("/auth/billing")
      .then((res) => res.data);
  }

  public async updateBillingProfile(body: {
    salutation: string;
    company: string;
    street: string;
    postal_code: string;
    city: string;
    country: string;
    vat_id?: string;
    billing_email: string;
    project_count: number;
    message?: string;
  }) {
    return this.axios
      .put<{
        billing: {
          salutation: string;
          company: string;
          street: string;
          postal_code: string;
          city: string;
          country: string;
          vat_id: string;
          billing_email: string;
          project_count: number;
          message: string;
        };
      }>("/auth/billing", body)
      .then((res) => res.data);
  }

  public async approveUser(id: string) {
    return this.axios.post(`/user_managements/user-approve/${id}`).then((res) => res.data);
  }

  public async rejectUser(id: string) {
    return this.axios.post(`/user_managements/user-reject/${id}`).then((res) => res.data);
  }

  public async confirmMembership(id: string) {
    return this.axios
      .post(`/user_managements/user-confirm-membership/${id}`)
      .then((res) => res.data);
  }

  public async declineMembership(id: string) {
    return this.axios
      .post(`/user_managements/user-decline-membership/${id}`)
      .then((res) => res.data);
  }

  public async listBillingRequests() {
    return this.axios.get("/user_managements/billing-requests").then((res) => res.data);
  }

  public async markBillingInvoiced(id: string) {
    return this.axios
      .post(`/user_managements/billing-requests/${id}/invoiced`)
      .then((res) => res.data);
  }

  public async forgotPassword(email: string) {
    return this.axios
      .post("/auth/generate-new-password", {
        email,
      })
      .then((res) => res.data)
      .catch((error) => error);
  }

  public async resetPassword(token: string, password: string) {
    return this.axios
      .post("/auth/reset-password", {
        token,
        password,
      })
      .then((res) => res.data)
      .catch((error) => error);
  }

  public async changePassword(
    account_id: string,
    old_password: string,
    new_password: string
  ) {
    return this.axios
      .post("/auth/update-password", {
        account_id,
        old_password,
        new_password,
      })
      .then((res) => res.data);
  }

  public async deleteOwnAccount() {
    return this.axios.post("/auth/delete-account", { confirm: "delete" });
  }

  public async getDisplayFormat() {
    return this.axios
      .get<{ display_format: "eu" | "us" }>("/auth/display-format")
      .then((res) => res.data)
      .catch((error) => error);
  }

  public async updateDisplayFormat(displayFormat: "eu" | "us") {
    return this.axios
      .put<{ display_format: "eu" | "us" }>("/auth/display-format", {
        display_format: displayFormat,
      })
      .then((res) => res.data)
      .catch((error) => error);
  }

  public async getProjects() {
    return this.axios.get<Project[]>("/projects");
  }
  public async getAccounts() {
    return this.axios.get<Account[]>("/accounts");
  }

  public saveGoogleAuth({ code, state }: { code: string; state: string }) {
    return this.axios.post<{ url: string }>("/auth/google/callback", {
      code,
      state,
    });
  }

  public getGoogleAuthURL() {
    return this.axios.get<{ url: string }>("/auth/google");
  }

  public async getGoogleAuths() {
    return this.axios.get<GoogleAuthentication[]>("/google-authentications");
  }

  public async deleteGoogleAuth(authId: string) {
    return this.axios.delete(`/google-authentications/${authId}`);
  }

  public async getGoogleProperties(authId: string): Promise<GoogleProperty[]> {
    const props = await this.axios.post<GoogleProperties>(
      "/google-properties",
      {
        auth_id: authId,
      }
    );
    return accountPropertiesFromResponse(props.data?.siteEntry).map((property) => ({
      siteUrl: property.siteUrl,
      permissionLevel: property.permissionLevel || "",
    }));
  }
  public async getBrandQueryExamples(params: {
    auth_id: string;
    country: string;
    property_url: string;
    homepage_url: string;
    excludes?: string[];
  }) {
    return await this.axios.post<BrandQueryExamplesResponse>(
      "/google-search-analytics/brand-exclude",
      { ...params }
    );
  }

  public async createProject(params: {
    auth_id: string;
    country: string;
    brand_queries: string[];
    name: string;
    properties: string[];
  }) {
    return await this.axios.post<Project>("/projects", { ...params });
  }

  public async deleteProject({ projectId }: { projectId: string }) {
    return this.axios.delete(`/projects/${projectId}`);
  }

  public async getProject({ projectId }: { projectId: string }) {
    return this.axios.get(`/projects/${projectId}`);
  }

  public async deleteKeywordMap({ projectId }: { projectId: string }) {
    return this.axios.delete(`/delete-keyword-map/${projectId}`);
  }

  public async deleteKeywordMapList({
    projectId,
    keywordmapId,
  }: {
    projectId: string;
    keywordmapId: string;
  }) {
    return this.axios.delete(
      `/projects/${projectId}/keyword-map/${keywordmapId}`
    );
  }

  public async editProject(
    projectId: string,
    params: {
      country: string;
      brand_queries: string[];
      name: string;
      user_agent?: string;
      regex_filters?: {
        name: string;
        target: "query" | "url";
        operator: "includingRegex" | "excludingRegex";
        expression: string;
      }[];
      main_content_xpaths?: string[];
      notification_cc?: string;
      tags?: { id: string; value: string }[];
      customer_journey?: { id: string; value: string }[];
      // Sent only when the edit page attaches a new Google login.
      // A normal save omits it so the stored login is not cleared.
      auth_id?: string;
    },
    _project?: any
  ) {
    const { auth_id, ...fields } = params;
    try {
      return await this.axios.put<Project>(`/projects/${projectId}`, {
        ...fields,
        ...(auth_id ? { auth_id } : {}),
      });

    } catch (error) {
      return error;
    }
  }

  public async listAnnotations({ projectId }: { projectId: string }) {
    return this.axios.get<AnnotationList>(`/projects/${projectId}/annotations`);
  }

  public async createAnnotation({
    projectId,
    annotation,
  }: {
    projectId: string;
    annotation: AnnotationWrite;
  }) {
    return this.axios.post<Annotation>(`/projects/${projectId}/annotations`, annotation);
  }

  public async updateAnnotation({
    projectId,
    annotationId,
    annotation,
  }: {
    projectId: string;
    annotationId: string;
    annotation: AnnotationWrite;
  }) {
    return this.axios.put<Annotation>(
      `/projects/${projectId}/annotations/${encodeURIComponent(annotationId)}`,
      annotation
    );
  }

  public async deleteAnnotation({
    projectId,
    annotationId,
  }: {
    projectId: string;
    annotationId: string;
  }) {
    return this.axios.delete(`/projects/${projectId}/annotations/${encodeURIComponent(annotationId)}`);
  }

  public async createAnnotationGroup({
    projectId,
    name,
    color,
  }: {
    projectId: string;
    name: string;
    color: string;
  }) {
    return this.axios.post<AnnotationGroup>(`/projects/${projectId}/annotation-groups`, { name, color });
  }

  public async updateAnnotationGroup({
    projectId,
    groupId,
    name,
    color,
  }: {
    projectId: string;
    groupId: string;
    name: string;
    color: string;
  }) {
    return this.axios.put<AnnotationGroup>(
      `/projects/${projectId}/annotation-groups/${encodeURIComponent(groupId)}`,
      { name, color }
    );
  }

  public async deleteAnnotationGroup({
    projectId,
    groupId,
  }: {
    projectId: string;
    groupId: string;
  }) {
    return this.axios.delete(
      `/projects/${projectId}/annotation-groups/${encodeURIComponent(groupId)}`
    );
  }

  public async exportAnnotations({ projectId }: { projectId: string }) {
    return this.axios.get<AnnotationPackage>(`/projects/${projectId}/annotations/export`);
  }

  public async importAnnotations({
    projectId,
    mode,
    pkg,
  }: {
    projectId: string;
    mode: "merge" | "replace";
    pkg: AnnotationPackage;
  }) {
    return this.axios.post<{
      domain: string;
      mode: string;
      addedGroups?: number;
      addedAnnotations?: number;
      groups?: number;
      annotations?: number;
    }>(`/projects/${projectId}/annotations/import`, { mode, package: pkg });
  }

  public async downloadContentchecksExport(params: { projectId: string }) {
    return this.axios.get(
      `projects/${params.projectId}/exports/contentchecks`,
      { responseType: "arraybuffer" }
    );
  }

  /** One content check in the same workbook as the project XLSX export. */
  public async downloadContentcheckExport(params: {
    projectId: string;
    contentcheckId: string;
  }) {
    return this.axios.get(
      `projects/${params.projectId}/contentchecks/${params.contentcheckId}/exports/xlsx`,
      { responseType: "arraybuffer" }
    );
  }
  public async downloadAdhocExport(params: { adhocExportId: string }) {
    return this.axios.get(`ad-hoc/${params.adhocExportId}/download`, {
      responseType: "arraybuffer",
    });
  }

  public async downloadContentcheckSearchQueries(params: {
    projectId: string;
    contentcheckId: string;
  }) {
    return this.axios.get(
      `projects/${params.projectId}/contentchecks/${params.contentcheckId}/exports/search-queries`,
      { responseType: "arraybuffer" }
    );
  }

  public async downloadContentcheckSearchTerms(params: {
    projectId: string;
    contentcheckId: string;
    type: string;
  }) {
    return this.axios.get(
      `projects/${params.projectId}/contentchecks/${params.contentcheckId}/exports/terms?table=${params.type}`,
      { responseType: "arraybuffer" }
    );
  }

  public async downloadApiScheduleRawExport(params: { exportId: string }) {
    return this.axios.get(`raw-exports/${params.exportId}/download`, {
      responseType: "arraybuffer",
    });
  }
  public async downloadContentcheckNewIntents(params: {
    projectId: string;
    contentcheckId: string;
  }) {
    return this.axios.get(
      `projects/${params.projectId}/contentchecks/${params.contentcheckId}/exports/new-intents`,
      { responseType: "arraybuffer" }
    );
  }

  public async downloadNewIntents(params: { projectId: string }) {
    return this.axios.get(`projects/${params.projectId}/exports/new-intents`, {
      responseType: "arraybuffer",
    });
  }

  public async getContentchecks(params: { projectId: string }) {
    return await this.axios.get<Contentcheck[]>(
      `/projects/${params.projectId}/contentchecks`
    );
  }
  public async getGoogleSearchIncidents() {
    return await this.axios.get<GoogleSearchIncidentsResponse>("/google-search-incidents");
  }

  public async getAPISchedules(params: { projectId: string }) {
    return await this.axios.get<APISchedule[]>(
      `/query-schedule/${params.projectId}`
    );
  }

  // Stored Global sparklines for the dashboard. API Scheduler writes the rows.
  public async getDashboardGlobalCharts() {
    return await this.axios.get<DashboardGlobalChart[]>("/dashboard/global-charts");
  }

  // Stored rank-tracking visibility index for each project. Rank tracking writes the days.
  public async getDashboardVisibilityCharts() {
    return await this.axios.get<DashboardVisibilitySeries[]>("/dashboard/visibility-charts");
  }
  public async getAdhocExports(params: { projectId: string }) {
    return await this.axios.get<AdhocExport[]>(
      `/ad-hoc/${params.projectId}`
    );
  }

  public async deleteAPISchedule(params: {
    projectId: string;
    apiScheduleId: string;
  }) {
    return await this.axios.delete<null>(
      `/query-schedule/${params.projectId}/${params.apiScheduleId}`
    );
  }

  public async createAdhocExport(params: {
    projectId: string;
    country: Dimension;
    device?: Dimension;
    page: Dimension;
    dimensions: string[];
    excludeBrandQueries?: number;
    queries?: Dimension[];
    searchType: string;
    dates: { start: string; end: string };
    rowLimit: number;
    property_site_url?: string;
    cluster_queries?: boolean;
  }) {
    //if (params.searchType !== "google_news" && params.searchType !== "discover") {
    //  completeparams.queries= params.queries
    //  completeparams.exclude_brand_queries= params.excludeBrandQueries
    //}

    //if (params.searchType !== "discover"){
    //  completeparams.device= params.device
    //}
    try {
      const { property_site_url, ...rest } = params;
      const response = await this.axios.post<{ message: string }>(`/ad-hoc`, {
        ...rest,
        ...propertyField(property_site_url),
      });
      this.noteModuleReportCreated("ad_hoc_export");
      return response;
    } catch (error) {
      return error
    }
  }
  public async getTrafficInsights(params: { projectId: string }) {
    return await this.axios.get<TrafficInsight[]>(
      `/traffic-insights/${params.projectId}`
    );
  }
  public async trafficTrend(
    projectID: string,
    payload: {
      dimension: "query" | "page";
      /** ISO3 code; omit or leave unset for all countries (no GSC country filter). */
      country?: string;
      start: string;
      end: string;
      compareStart: string;
      compareEnd: string;
      /**
       * Optional GSC dimension filter.
       * Pages omit it for All. Query All sends a match-all include so the
       * stored report is not limited to the Brand regex.
       */
      filter?:
        | { preset: string }
        | { mode: "include" | "exclude"; expression: string };
      /**
       * Query requests only. Limits both the period and the comparison
       * to pages that match this URL pattern (for example contains "/Filiale").
       */
      page?: TrafficTrendPageFilter;
      property_site_url?: string;
      /** Per-period cap. Omitted requests use the API default of 100,000. */
      rowLimit?: number;
    }
  ) {
    const { property_site_url, ...rest } = payload;
    const response = await this.axios.post<TrafficTrendReport>(
      `/projects/${projectID}/traffic-trend`,
      { ...rest, ...propertyField(property_site_url) },
      { timeout: 180000 }
    );
    this.noteModuleReportCreated("traffic_trend");
    return response.data;
  }

  public async performanceDetails(
    projectID: string,
    payload: {
      search_types: string[];
      months: number;
      country: Dimension;
      device?: Dimension;
      page: Dimension;
      queries?: Dimension[];
      exclude_brand_queries: boolean;
      property_site_url?: string;
    }
  ) {
    const { property_site_url, ...rest } = payload;
    const response = await this.axios.post<PerformanceDetailsReport>(
      `/projects/${projectID}/performance-details`,
      { ...rest, ...propertyField(property_site_url) },
      { timeout: 180000 }
    );
    return response.data;
  }

  public async performanceDetailsCoverage(
    projectID: string,
    payload: {
      search_type: string;
      months: number;
      country: Dimension;
      device?: Dimension;
      page: Dimension;
      queries?: Dimension[];
      exclude_brand_queries: boolean;
      property_site_url?: string;
    }
  ) {
    const { property_site_url, ...rest } = payload;
    const response = await this.axios.post<QueryCoverage>(
      `/projects/${projectID}/performance-details/coverage`,
      { ...rest, ...propertyField(property_site_url) },
      { timeout: 180000 }
    );
    return response.data;
  }

  public async getRankTracking(projectID: string) {
    const response = await this.axios.get<RankTrackingReport>(
      `/projects/${projectID}/rank-tracking`
    );
    return response.data;
  }

  public async getRankTrackingDashboard(projectID: string) {
    const response = await this.axios.get<RankDashboard>(
      `/projects/${projectID}/rank-tracking/dashboard`
    );
    return response.data;
  }

  public async getContentRankReport(projectID: string, monitorID: string) {
    const response = await this.axios.get<RankTrackingReport>(
      `/projects/${projectID}/rank-tracking/content/${monitorID}`
    );
    return response.data;
  }

  public async deleteContentRankMonitor(projectID: string, monitorID: string) {
    await this.axios.delete(`/projects/${projectID}/rank-tracking/content/${monitorID}`);
  }

  public async getContentRankMonitor(projectID: string, contentcheckID: string) {
    const response = await this.axios.get<{ monitor: ContentRankMonitorRef | null }>(
      `/projects/${projectID}/contentchecks/${contentcheckID}/rank-monitoring`
    );
    return response.data.monitor;
  }

  public async createContentRankMonitor(projectID: string, contentcheckID: string) {
    const response = await this.axios.post<{ id: string; name: string; created: boolean; fetched?: boolean }>(
      `/projects/${projectID}/contentchecks/${contentcheckID}/rank-monitoring`,
      undefined,
      { timeout: 180000 }
    );
    return response.data;
  }

  public async refreshRankSearchVolume(projectID: string) {
    const response = await this.axios.post<RankTrackingReport>(
      `/projects/${projectID}/rank-tracking/refresh-search-volume`,
      undefined,
      { timeout: 180000 }
    );
    return response.data;
  }

  public async saveRankTracking(
    projectID: string,
    payload: { country_mode: "focus" | "all"; queries: string[]; min_impressions: number }
  ) {
    const response = await this.axios.put<RankTrackingReport>(
      `/projects/${projectID}/rank-tracking`,
      payload,
      { timeout: 180000 }
    );
    return response.data;
  }

  public async rankTrackingSuggestions(
    projectID: string,
    filter: {
      page_operator: string;
      page: string;
      query_operator: string;
      query_pattern: string;
    }
  ) {
    const response = await this.axios.post<{ queries: RankTrackingSuggestion[] }>(
      `/projects/${projectID}/rank-tracking/suggestions`,
      filter,
      { timeout: 180000 }
    );
    return response.data.queries;
  }

  public async downloadRankTrackingExport(projectID: string, exportID: string) {
    return this.axios.get(
      `/projects/${projectID}/rank-tracking/exports/${exportID}/download`,
      { responseType: "arraybuffer" }
    );
  }

  public async downloadRankTrackingZip(projectID: string) {
    return this.axios.get(`/projects/${projectID}/rank-tracking/archive.zip`, {
      responseType: "arraybuffer",
    });
  }

  public async listTrafficTrends(projectID: string) {
    const response = await this.axios.get<TrafficTrendSummary[]>(
      `/projects/${projectID}/traffic-trend`
    );
    return response.data;
  }

  public async getTrafficTrend(projectID: string, reportID: string) {
    const response = await this.axios.get<TrafficTrendReport>(
      `/projects/${projectID}/traffic-trend/${reportID}`
    );
    return response.data;
  }

  public async deleteTrafficTrend(projectID: string, reportID: string) {
    await this.axios.delete(
      `/projects/${projectID}/traffic-trend/${reportID}`
    );
  }

  public async createTrafficInsight(params: {
    projectId: string;
    page: {
      operator: string,
      label: string,
    }
    excludeBrandQueries: boolean,
    device: string;
    start: Date;
    end: Date;
    minImpressions: number;
    rowLimit: number;
    property_site_url?: string;
  }) {
    try {
    const response = await this.axios.post<{ message: string }>(`/traffic-insights`, {
      project_id: params.projectId,
      page: { operator: params.page.operator, label: params.page.label },
      excludeBrandQueries: params.excludeBrandQueries,
      device: params.device,
      // Calendar days from the shared date-range picker. The stored report echoes these as start and end.
      start: format(params.start, "yyyy-MM-dd"),
      end: format(params.end, "yyyy-MM-dd"),
      min_impressions: params.minImpressions,
      row_limit: params.rowLimit,
      ...propertyField(params.property_site_url),
    });
    this.noteModuleReportCreated("traffic_insights");
    return response;
  } catch (error) {
      return error
  }
  }

  public async downloadTrafficInsight(params: { trafficInsightId: string }) {
    return this.axios.get(
      `traffic-insights/${params.trafficInsightId}/download`,
      {
        responseType: "arraybuffer",
      }
    );
  }

  public async getTrafficInsight(params: {
    projectId: string;
    trafficInsightId: string;
  }) {
    return await this.axios.get<TrafficInsight>(
      `/projects/${params.projectId}/traffic-insights/${params.trafficInsightId}`
    );
  }

  /**
   * One page of stored queries.
   * Each row is one query: top is the highest-click URL, urls are the other rankings.
   * strikingDistance=1 keeps queries whose top URL is in striking distance.
   * sitelinks=1 keeps queries that have a sitelink URL.
   * cannibalization=1 keeps queries that have cannibalization.
   * searchIn limits the search to the query or the URL. Omitted searches both.
   * Combined flags all apply.
   */
  public async getTrafficInsightRows(params: {
    projectId: string;
    trafficInsightId: string;
    page: number;
    search?: string;
    searchIn?: "query" | "page";
    sort?: string;
    strikingDistance?: boolean;
    sitelinks?: boolean;
    cannibalization?: boolean;
  }) {
    return await this.axios.get<TrafficInsightRowsPage>(
      `/projects/${params.projectId}/traffic-insights/${params.trafficInsightId}/rows`,
      {
        params: {
          page: params.page,
          search: params.search ?? "",
          searchIn: params.searchIn,
          sort: params.sort,
          strikingDistance: params.strikingDistance ? 1 : undefined,
          sitelinks: params.sitelinks ? 1 : undefined,
          cannibalization: params.cannibalization ? 1 : undefined,
        },
      }
    );
  }

  public async getOptimizationWizards(params: { projectId: string }) {
    return await this.axios.get<OptimizationWizard[]>(
      `/optimization-wizards/${params.projectId}`
    );
  }
  public async getOptimizationWizard(params: {
    projectId: string;
    optimizationWizardId: string;
  }) {
    return await this.axios.get<OptimizationWizard>(
      `/optimization-wizards/${params.projectId}/${params.optimizationWizardId}`
    );
  }

  public async createOptimizationWizard(params: {
    projectId: string;
    device: Dimension;
    page: Dimension;
    country: Dimension;
    searchType: string;
    maxResults: number;
    min_ctr: number;
    property_site_url?: string;
  }) {
    try {
          const response = await this.axios.post<{ message: string }>(
      `/optimization-wizards/${params.projectId}`,
      {
        device: params.device,
        page: params.page,
        country: params.country,
        search_type: "web",
        max_results: params.maxResults,
        min_ctr: params.min_ctr,
        ...propertyField(params.property_site_url),
      }
    );
    this.noteModuleReportCreated("optimization_wizard");
    return response;
  } catch (error) {
      return error
  }
  }
  public async getAPISchedule(params: {
    projectId: string;
    apiScheduleId: string;
  }) {
    return await this.axios.get<APISchedule>(
      `/query-schedule/${params.projectId}/${params.apiScheduleId}`
    );
  }

  public async createAPISchedule(payload: {
    projectID: string;
    title: string;
    description: string;
    excludeBrandQueries?: number | null;
    queries?: Dimension | null; // comma separated values -.-
    device: Dimension;
    country: Dimension;
    page: Dimension;
    dimensions: string[];
    frequency: string;
    aggregation_type: string;
    searchType: string;
    rowLimit: number;
    property_site_url?: string;
  }) {
    const completePayload: any = {
      projectID: payload.projectID,
      title: payload.title,
      description: payload.description,
      dimensions: payload.dimensions,
      // Copy, otherwise clearing the label below mutates the caller's form state.
      country: { ...payload.country },
      page: payload.page,
      row_limit: 0, // is not being used anymore.
      frequency: payload.frequency,
      aggregation_type: payload.aggregation_type,
      search_type: payload.searchType,
      rowLimit: payload.rowLimit,
      ...propertyField(payload.property_site_url),
    };

    if (
      payload.searchType !== "google_news" &&
      payload.searchType !== "discover"
    ) {
      completePayload.queries = payload.queries;
      completePayload.exclude_brand_queries = payload.excludeBrandQueries;
    }

    if (payload.searchType !== "discover") {
      completePayload.device = payload.device;
    }

    if (payload.country.operator == "nofilter") {
      completePayload.country.label = ""
    }

    const path = `/query-schedule`;
    try {
      const response = await this.axios.post<APISchedule>(path, completePayload);
      this.noteModuleReportCreated("api_scheduler");
      return response;
    } catch (error) {
      return error
    }
  }

  public async updateAPISchedule(params: {
    title: string;
    description: string;
    frequency: string;
    projectId: string;
    scheduleId: string;
  }) {
    return await this.axios.put<Project>(
      `/query-schedule/${params.projectId}/${params.scheduleId}`,
      { ...params }
    );
  }

  public async refetchAPISchedule(params: {
    projectId: string;
    scheduleId: string;
    from: string;
  }) {
    return await this.axios.post<{ from: string }>(
      `/query-schedule/${params.projectId}/${params.scheduleId}/refetch`,
      { from: params.from }
    );
  }
  public async getContentcheck(params: {
    projectId: string;
    contentcheckId: string;
  }) {
    return await this.axios.get<Contentcheck>(
      `/projects/${params.projectId}/contentchecks/${params.contentcheckId}`
    );
  }
  public async deleteContentcheck(params: {
    projectId: string;
    contentcheckId: string;
  }) {
    return await this.axios.delete<null>(
      `/projects/${params.projectId}/contentchecks/${params.contentcheckId}`
    );
  }

  public async deleteOptimizationWizards(params: {
    projectId: string;
    optimizationWizardsId: string;
  }) {
    return await this.axios.delete<null>(
      `/projects/${params.projectId}/optimization-wizards/${params.optimizationWizardsId}`
    );
  }

  public async deleteAdhocExports(params: {
    projectId: string;
    adhocExportId: string;
  }) {
    return await this.axios.delete<null>(
      `/projects/${params.projectId}/ad-hoc/${params.adhocExportId}`
    );
  }

  public async deleteTrafficInsights(params: {
    projectId: string;
    trafficInsightId: string;
  }) {
    return await this.axios.delete<null>(
      `/projects/${params.projectId}/traffic-insights/${params.trafficInsightId}`
    );
  }

  public async updateContentcheck(params: {
    projectId: string;
    contentcheck: Contentcheck;
  }) {
    return await this.axios.put<Contentcheck>(
      `/projects/${params.projectId}/contentchecks/${params.contentcheck.id}`,
      { type: "save", ...params.contentcheck }
    );
  }

  public async updateSource(params: {
    projectId: string;
    contentcheck: Contentcheck;
  }) {
    return await this.axios.put<Contentcheck>(
      `/projects/${params.projectId}/contentchecks/${params.contentcheck.id}`,
      { type: "overwrite", ...params.contentcheck }
    );
  }

  public async addNewDataSet(
    projectID: string,
    payload: {
      url: string;
      isPrioURL: string;
      topKeyword: string;
      customerJourneyPhase: string;
      tags: string[];
      secondaryKeywords: string;
    }
  ) {
    const data = {
      project_id: projectID,
      url: payload.url,
      prio_url: payload.isPrioURL,
      top_keyword: payload.topKeyword,
      customer_journey: payload.customerJourneyPhase,
      tags: payload.tags,
      keyword: payload.secondaryKeywords,
    };

    try {
      await this.axios.post<Contentcheck>(`/keyword-map/add`, data);
    } catch (error) {
      return error;
    }
  }

  public async getKeywordMap(
    projectId: string,
    query: any,
    currentPage: number,
    sortBy: string
  ) {
    // Let axios encode the values: the search term is free text and may contain
    // &, #, + or = which would otherwise change the query string.
    // Listing does not refresh. Call refreshKeywordMap for Search Console work.
    return await this.axios
      .get(`/keyword-map/get/${projectId}`, {
        params: {
          search: query.searchValue ?? "",
          terms: query.searchDropdownValue ?? "",
          page: currentPage,
          condition: query.includeExclude ?? "",
          header: sortBy ?? "",
        },
      })
      .then((res) => res.data);
  }

  public async refreshKeywordMap(projectId: string) {
    try {
      return await this.axios
        .post(`/keyword-map/${projectId}/refresh`)
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async getKeywordMapList(params: { projectId: string }) {
    return await this.axios.get(`/projects/${params.projectId}/keyword-map`);
  }

  public async getAllUsers() {
    return await this.axios
      .get(`/user_managements/user-list`)
      .then((res) => res.data);
  }

  public async getRegexPresets() {
    return await this.axios.get(`/regex-presets`).then((res) => res.data);
  }

  public async getNotificationTemplates() {
    return await this.axios
      .get<NotificationTemplate[]>(`/admin/notification-templates`)
      .then((res) => res.data);
  }

  public async updateNotificationTemplate(template: NotificationTemplate) {
    return await this.axios
      .put<NotificationTemplate>(`/admin/notification-templates/${template.id}`, {
        name: template.name,
        subject: template.subject,
        body_html: template.body_html,
        cadence: template.cadence,
        weekday: template.weekday,
        send_time: template.send_time,
        delay_days: template.delay_days,
        anchor_key: template.anchor_key,
        enabled: template.enabled,
      })
      .then((res) => res.data);
  }

  public async sendNotificationTest(
    id: string,
    projectId: string,
    account?: NotificationAccountSample
  ) {
    return await this.axios
      .post<{ message: string }>(`/admin/notification-templates/${id}/test`, {
        project_id: projectId,
        // Credentials-mail tests substitute these samples. The API still
        // delivers the message to the signed-in admin.
        ...(account
          ? { name: account.name, email: account.email, password: account.password }
          : {}),
      })
      .then((res) => res.data);
  }

  public async sendNotificationNow(id: string) {
    return await this.axios
      .post<{ sent: number; skipped: number; failed: number; already: number }>(
        `/admin/notification-templates/${id}/send-now`
      )
      .then((res) => res.data);
  }

  public async getNotificationProjects() {
    return await this.axios
      .get<NotificationProjectOption[]>(`/admin/notification-projects`)
      .then((res) => res.data);
  }

  public async getModuleReportCounts(now = new Date()) {
    const response = await this.axios.get("/admin/module-report-counts");
    return parseModuleReportCounts(response.data, now);
  }

  // Counts a created report. The body is only the module, so the stored row
  // cannot identify the account or property. A failed counter must not block
  // the report itself, and must not sign the user out.
  private noteModuleReportCreated(module: ReportModule) {
    const body = moduleReportCountBody(module);
    void this.axios
      .post("/module-report-counts", body, {
        skipAuthRedirect: true,
      } as AxiosRequestConfig)
      .catch(() => undefined);
  }

  public async getNotificationPreferences() {
    return await this.axios
      .get<NotificationPreferences>(`/auth/notification-preferences`)
      .then((res) => res.data);
  }

  public async updateNotificationPreferences(preferences: {
    unsubscribe_all: boolean;
    unsubscribed: Record<string, boolean>;
  }) {
    return await this.axios
      .put<NotificationPreferences>(`/auth/notification-preferences`, preferences)
      .then((res) => res.data);
  }

  public async previewUnsubscribe(token: string) {
    return await this.axios
      .get<{ scope: string; template_name: string }>(`/notifications/unsubscribe`, {
        params: { token },
      })
      .then((res) => res.data);
  }

  public async applyUnsubscribe(token: string) {
    return await this.axios
      .post<{ message: string }>(`/notifications/unsubscribe`, { token })
      .then((res) => res.data);
  }

  public async createRegexPresets(presets: RegexPresetInput[]) {
    try {
      return await this.axios
        .post(`/regex-presets`, { presets })
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async updateRegexPreset(id: string, preset: RegexPresetInput) {
    try {
      return await this.axios
        .put(`/regex-presets/${id}`, preset)
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async deleteRegexPreset(id: string) {
    try {
      return await this.axios
        .delete(`/regex-presets/${id}`)
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async starTopKeyword(id: any) {
    return await this.axios
      .put(`/keyword-map/update-keyword`, { id })
      .then((res) => res.data);
  }

  public async uploadExcelFile(formData: any) {
    try {
      return await this.axios
        .post(`/stop-words/add`, formData)
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async deleteKeyword(rowId: string, type: string) {
    try {
      return await this.axios
        .delete(`/keyword-delete/${rowId}/${type}`)
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async deleteStopWord(id: string) {
    try {
      return await this.axios
        .delete(`/delete-stop-words/${id}`)
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async dropAll(id: string) {
    try {
      return await this.axios.delete(`/drop-all/${id}`).then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async addCrawler(data: any, id: string) {
    try {
      return await this.axios

        .post("/settings/add-crawler", {
          user_agent: data.default_user_agent,
          delay: Number(data.crawl_delay),
          firecrawl_api_key: data.firecrawl_api_key || "",
          clear_firecrawl_api_key: Boolean(data.clear_firecrawl_api_key),
          created_by: id,
        })
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async getCrawler() {
    try {
      return await this.axios
        .get("/settings/get-crawler")
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async deleteUser(id: string) {
    try {
      return await this.axios
        .delete(`user_managements/user-delete/${id}`)
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async addNewUser(data: any) {
    try {
      return await this.axios
        .post(`user_managements/user-create`, {
          email: data.email,
          name: data.name,
          password: data.password,
          project_limit: Number(data.project_limit),
          account_type: data.account_type
        })
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async editUser(data: any, id: any) {
    try {
      return await this.axios
        .post(`user_managements/user-update`, {
          name: data.name,
          password: data.password,
          project_limit: Number(data.project_limit),
          account_type: data.account_type,
          id: id,
        })
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async importCSV(formData: FormData) {
    try {
      return await this.axios
        .post(`/keyword-map-bulk-upload`, formData)
        .then((res) => res.data);
    } catch (error) {
      return error;
    }
  }
  public async updateTags(newData: any) {
    try {
      return await this.axios.put(
        `/keyword-map/update-tags`,
        { ...newData }
      );
    } catch (error) {
      return error;
    }
  }

  public async bulkAssignKeywordMap(payload: {
    project_id: string;
    mode: "tag" | "journey";
    tag_id?: string;
    customer_journey?: string;
    ids?: string[];
    search?: string;
    terms?: string;
    condition?: string;
    prio?: string;
    preview?: boolean;
  }) {
    try {
      return await this.axios.put(`/keyword-map/bulk-assign`, payload).then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async updateChild(data: any) {
    try {
      return await this.axios.put(
        `/keyword-map/update-child`,
        { ...data }
      );
    } catch (error) {
      return error;
    }
  }

  public async updateParent(data: any) {
    return await this.axios.put(
      `/keyword-map/update-parent`,
      { ...data }
    );
  }

  public async updateName(id: any, name: any) {
    try {
      return await this.axios.post(
        `/user_managements/user-update`,
        { id, name }
      );
    } catch (error) {
      return error;
    }
  }

  public async refreshKeywordMapUrl(projectId: string, keywordMapId: string) {
    return await this.axios
      .post(`/keyword-map/refresh-url`, { project_id: projectId, id: keywordMapId })
      .then((res) => res.data)
      .catch((error) => error);
  }

  public async addBlankRow(data: any) {
    return await this.axios
      .post(
        `/keyword-map/add-blank-row`,
        { ...data }
      )
      .catch((error) => error);
  }

  public async getByCountry(country: any) {
    return await this.axios
      .get(
        `/get-stop-words-list/${country}`
      )
      .catch((error) => error);
  }

  public async getStopWordsByCountry(country: any) {
    return await this.axios
      .get(`${apiRoot}/get-stop-words/${country}`, { baseURL: "" })
      .catch((error) => error);
  }

  public async createKeywordMap(
    projectID: string,
    payload: {
      period: string;
      page: {
        operator: string;
        label: string;
      };
      minClicks: string;
      minImpressions: string;
      rowLimit: number;
      property_site_url?: string;
    }
  ) {
    let End = endOfMonth(sub(new Date(), { months: 1 }));
    let Start =
      payload.period == "last_month"
        ? startOfMonth(sub(new Date(), { months: 1 }))
        : startOfMonth(sub(new Date(), { months: 3 }));

    const data = {
      projectID: projectID,
      Dates: {
        Start: format(Start, "yyyy-MM-dd"),
        End: format(End, "yyyy-MM-dd"),
      },
      page: { operator: payload.page.operator, label: payload.page.label },
      minClicks: Number(payload.minClicks),
      minImpressions: Number(payload.minImpressions),
      rowLimit: Number(payload.rowLimit),
      ...propertyField(payload.property_site_url),
    };
    try {
      const response = await this.axios.post<Contentcheck>(`/keyword-map/create`, data);
      this.noteModuleReportCreated("keyword_map");
      return response;
    } catch (error) {
      return error;
    }
  }

  public async createContentcheck(
    projectID: string,
    payload: {
      url: string;
      dates: { start: Date; end: Date };
      excludeBrandQueries: boolean;
      excludeQueries: string[];
      minImpressions: string;
      minPosition: string;
      maxPosition: string;
      device: Dimension;
      country: Dimension;
      min_ctr?: number;
      property_site_url?: string;
    }
  ) {
    const formattedDates = {
      start: format(payload.dates.start, "yyyy-MM-dd"),
      end: format(payload.dates.end, "yyyy-MM-dd"),
    };

    // Search Analytics is not called in this repo. The API queries
    // property_site_url with this page string. The URL is not rewritten.
    // equals compares the typed URL with the page Search Console stored.
    const url = payload.url.trim();
    const page = contentCheckPageFilter(url);

    // "All" hides the country select but leaves the profile country in the
    // label. The query uses no country filter, so do not store that code.
    // Copy first so the caller's form state stays unchanged.
    const country =
      payload.country?.operator === "nofilter"
        ? { ...payload.country, label: "" }
        : payload.country;

    const completePayload = {
      url,
      page,
      country,
      min_impressions: parseInt(payload.minImpressions, 10),
      max_position: parseInt(payload.maxPosition, 10),
      min_position: parseInt(payload.minPosition, 10),
      device: payload.device,
      exclude_queries: payload.excludeQueries,
      exclude_brand_queries: payload.excludeBrandQueries,
      projectID,
      dates: formattedDates,
      min_ctr: payload.min_ctr,
      ...propertyField(payload.property_site_url),
    };

    const response = await this.axios.post<Contentcheck>(
      `/projects/${projectID}/contentchecks`,
      completePayload
    );
    this.noteModuleReportCreated("content_optimization");
    return response;
  }

  public async createContentDecay(
    projectID: string,
    payload: {
      title: string;
      country: Dimension;
      excludeBrandQueries: boolean;
      page: Dimension;
      device: Dimension;
      minimumClicks: number;
      property_site_url?: string;
    }
  ) {
    try {  
    const { property_site_url, ...rest } = payload;
    const response = await this.axios.post<ContentDecay>(
      `/projects/${projectID}/content-decay`,
      { ...rest, ...propertyField(property_site_url) }
    );
    this.noteModuleReportCreated("content_decay");
    return response;
  } catch (error) {
      return error
  }
  }

  public async getContentDecayList(params: { projectId: string }) {
    return await this.axios.get<ContentDecay[]>(
      `/projects/${params.projectId}/content-decay`
    );
  }

  public async getContentDecay(params: any) {
    // Let axios encode the values: the search term is free text and may contain
    // &, #, + or = which would otherwise change the query string.
    return await this.axios.get<ContentDecay>(
      `/projects/${params.projectId}/content-decay/${params.contentdecayId}`,
      {
        params: {
          search: params.query.searchValue ?? "",
          terms: params.query.searchDropdownValue ?? "",
          page: params.currentPage,
          condition: params.query.includeExclude ?? "",
          header: params.header || undefined,
          start: params.startingPeriod || undefined,
          compare: params.comparedPeriod || undefined,
          metric: params.metric || undefined,
          // Positive minDecline is a minimum decline. A negative value is a
          // minimum increase of the same magnitude (delta_*-desc sorts).
          // Leave the param off only when the caller did not set it.
          minDecline: params.minDecline != null ? params.minDecline : undefined,
        },
      }
    );
  }

  public async updateTitle({ title, projectId, contentdecayId }: any) {
    try {
      return await this.axios.put(
        `/projects/${projectId}/content-decay/${contentdecayId}`,
        { title }
      );
    } catch (error) {
      return error;
    }
  }

  public async deleteContentDecay(params: {
    projectId: string;
    contentdecayId: string;
  }) {
    return await this.axios.delete<null>(
      `/projects/${params.projectId}/content-decay/${params.contentdecayId}`
    );
  }

  public async exportContentDecayTable(params: { contentdecayId: string, projectId: string }) {
    return this.axios.get(`/content-decay/${params.contentdecayId}/download-csv`, {
      responseType: "arraybuffer",
    });
  }

  public async exportKeywordMapTable(params: { projectId: string}) {
    return this.axios.get(`/keyword-map/${params.projectId}/download-keyword-map`, {
      responseType: "arraybuffer",
    });
  }

  public async exportKeywordsTable(params: { projectId: string}) {
    return this.axios.get(`/keyword-map/${params.projectId}/download-keywords`, {
      responseType: "arraybuffer",
    });
  }

  public async getFreeAccountSettings() {
    try {
      return await this.axios.get("/account-setting").then((res) => res.data);
    } catch (error) {
      return error;
    }
  }

  public async saveFreeAccountSettings(data: any) {
    return await this.axios.post(`/account-setting`, data);
  }

  public async resyncCD(projectId: string) {
    return await this.axios.get(`/cd-update/${projectId}`);
  }

  public async createUrlInspection(params: {
    projectId: string;
    urls: string;
    source: string;
    insert: "start" | "end";
    monitor: "top" | "normal" | "once";
    runNow?: boolean;
  }) {
    return this.axios.post<{
      batch: URLInspectionBatch;
      rejected: URLInspectionReject[];
    }>(`/projects/${params.projectId}/url-inspections`, {
      urls: inspectionUrlsForApi(params.urls),
      source: params.source,
      insert: params.insert,
      monitor: params.monitor,
      run_now: params.runNow,
    });
  }

  public async saveUrlInspectionSettings(params: {
    projectId: string;
    dailyLimit: number;
    startTime: string;
  }) {
    return this.axios.put<URLInspectionSettings>(
      `/projects/${params.projectId}/url-inspection-settings`,
      {
        daily_limit: params.dailyLimit,
        start_time: params.startTime,
      }
    );
  }

  public async searchMonitoredUrls(params: { projectId: string; pattern: string }) {
    return this.axios.get<{ total: number; urls: MonitoredURL[] }>(
      `/projects/${params.projectId}/url-inspections/monitored`,
      { params: { pattern: exactUserUrl(params.pattern) } }
    );
  }

  public async deleteMonitoredUrls(params: {
    projectId: string;
    pattern?: string;
    inspectedUrlIds?: string[];
  }) {
    // axios 0.20 drops `data` on .delete(), so the API sees an empty body and returns EOF.
    return this.axios.request<{ deleted: number }>({
      method: "DELETE",
      url: `/projects/${params.projectId}/url-inspections/monitored`,
      data: {
        pattern: params.pattern == null ? undefined : exactUserUrl(params.pattern),
        inspected_url_ids: params.inspectedUrlIds,
      },
    });
  }

  public async listWaitingUrls(params: {
    projectId: string;
    pattern?: string;
    sort?: "url" | "monitor" | "property_site_url";
    dir?: "asc" | "desc";
  }) {
    return this.axios.get<{ total: number; urls: WaitingURL[] }>(
      `/projects/${params.projectId}/url-inspections/waiting`,
      {
        params: {
          pattern: exactUserUrl(params.pattern) || undefined,
          ...(params.sort ? { sort: params.sort, dir: params.dir } : {}),
        },
      }
    );
  }

  public async deleteWaitingUrls(params: {
    projectId: string;
    jobIds?: string[];
    pattern?: string;
    all?: boolean;
  }) {
    // axios 0.20 drops `data` on .delete(), so the API sees an empty body and returns EOF.
    return this.axios.request<{ deleted: number }>({
      method: "DELETE",
      url: `/projects/${params.projectId}/url-inspections/waiting`,
      data: {
        job_ids: params.jobIds,
        pattern: params.pattern == null ? undefined : exactUserUrl(params.pattern),
        all: params.all,
      },
    });
  }

  public async runWaitingUrls(params: {
    projectId: string;
    jobIds?: string[];
    pattern?: string;
    all?: boolean;
  }) {
    return this.axios.post<{ queued: number }>(
      `/projects/${params.projectId}/url-inspections/waiting/run`,
      {
        job_ids: params.jobIds,
        pattern: params.pattern == null ? undefined : exactUserUrl(params.pattern),
        all: params.all,
      }
    );
  }

  public async listUrlInspectionChanges(params: { projectId: string; monitor?: string }) {
    return this.axios.get<{ total: number; changes: URLInspectionChange[] }>(
      `/projects/${params.projectId}/url-inspections/results/changes`,
      { params: { monitor: params.monitor || undefined } }
    );
  }

  public async getUrlInspectionUrlHistory(params: { projectId: string; inspectedUrlId: string }) {
    return this.axios.get<URLInspectionURLHistory>(
      `/projects/${params.projectId}/url-inspections/results/${params.inspectedUrlId}/history`
    );
  }

  public async moveMonitoredUrls(params: {
    projectId: string;
    inspectedUrlIds: string[];
    monitor: "top" | "normal" | "once";
  }) {
    return this.axios.post<{
      updated: number;
      skipped: number;
      rejected: { url: string; reason: string }[];
    }>(`/projects/${params.projectId}/url-inspections/monitored/monitor`, {
      inspected_url_ids: params.inspectedUrlIds,
      monitor: params.monitor,
    });
  }

  public async runMonitoredUrls(params: { projectId: string; inspectedUrlIds: string[] }) {
    return this.axios.post<{ queued: number; skipped: number }>(
      `/projects/${params.projectId}/url-inspections/monitored/run`,
      { inspected_url_ids: params.inspectedUrlIds }
    );
  }

  public async getUrlInspectionQueue(projectId: string) {
    return this.axios.get<URLInspectionQueue>(
      `/projects/${projectId}/url-inspections/queue`
    );
  }

  public async getUrlInspectionHistory(projectId: string, monitor?: string) {
    // Empty / omitted monitor requests All (aggregated) history from the API.
    return this.axios.get<URLInspectionHistory>(
      `/projects/${projectId}/url-inspections/results/history`,
      { params: monitor ? { monitor } : {} }
    );
  }

  public async getUrlInspectionSummary(params: {
    projectId: string;
    monitor?: string;
    indexingState?: string;
    coverageState?: string;
    canonicalMatch?: string;
    verdict?: string;
    crawledAs?: string;
    propertySiteUrl?: string;
    group?: string;
  }) {
    return this.axios.get<URLInspectionSummary>(
      `/projects/${params.projectId}/url-inspections/results/summary`,
      { params: inspectionResultParams(params) }
    );
  }

  public async fetchUrlInspectionSitemaps(projectId: string, urls = "") {
    return this.axios.post<{ urls: string[]; rejected?: { line: number; url: string; reason: string }[] }>(
      `/projects/${projectId}/url-inspections/sitemaps`,
      { urls: inspectionUrlsForApi(urls) }
    );
  }

  public async listUrlInspectionResultOptions(params: { projectId: string; monitor?: string }) {
    return this.axios.get<URLInspectionResultOptions>(
      `/projects/${params.projectId}/url-inspections/results/options`,
      { params: { monitor: params.monitor || undefined } }
    );
  }

  public async listUrlInspectionResults(params: {
    projectId: string;
    indexingState?: string;
    coverageState?: string;
    canonicalMatch?: string;
    verdict?: string;
    crawledAs?: string;
    propertySiteUrl?: string;
    group?: string;
    monitor?: string;
    page?: number;
    pageSize?: number;
    crawlBucket?: string;
    changes?: boolean;
    changeKind?: string;
  }) {
    return this.axios.get<URLInspectionResultPage>(
      `/projects/${params.projectId}/url-inspections/results`,
      {
        params: inspectionResultParams(params),
      }
    );
  }

  public async downloadUrlInspectionResults(params: {
    projectId: string;
    projectName?: string;
    indexingState?: string;
    coverageState?: string;
    canonicalMatch?: string;
    verdict?: string;
    crawledAs?: string;
    propertySiteUrl?: string;
    group?: string;
    monitor?: string;
    format?: "csv" | "xlsx";
    raw?: boolean;
    crawlBucket?: string;
    changes?: boolean;
    changeKind?: string;
    signal?: AbortSignal;
    onProgress?: (loaded: number) => void;
  }): Promise<AuthorizedDownload> {
    const path = `/projects/${params.projectId}/url-inspections/results/export`;
    if (urlHasBlankPathSegment(path)) {
      throw new Error("Refusing a request with a blank project id.");
    }
    const search = inspectionExportSearch(params);
    const base = (this.axios.defaults?.baseURL || "").replace(/\/$/, "");
    const qs = search.toString();
    const url = `${base}${path}${qs ? `?${qs}` : ""}`;
    return downloadAuthorizedFile({
      url,
      headers: { Authorization: `Bearer ${getUserToken()}` },
      suggestedName: downloadFilename(
        params.projectName || "Project",
        params.raw ? "URL-Inspection-Raw" : "URL-Inspection",
        params.format === "xlsx" ? ".xlsx" : ".csv"
      ),
      signal: params.signal,
      onProgress: params.onProgress,
      browserDownload: async () => {
        const ticket = await this.axios.get<{ ticket: string }>(
          `/projects/${params.projectId}/url-inspections/results/export-ticket`,
          { params: Object.fromEntries(search.entries()) }
        );
        const target = new URL(url, window.location.origin);
        target.searchParams.set("ticket", ticket.data.ticket);
        const link = document.createElement("a");
        link.href = target.toString();
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
    });
  }

}

function inspectionResultParams(params: {
  indexingState?: string;
  coverageState?: string;
  canonicalMatch?: string;
  verdict?: string;
  crawledAs?: string;
  propertySiteUrl?: string;
  group?: string;
  monitor?: string;
  page?: number;
  pageSize?: number;
  crawlBucket?: string;
  changes?: boolean;
  changeKind?: string;
}) {
  return {
    indexing_state: params.indexingState || undefined,
    coverage_state: params.coverageState || undefined,
    canonical_match: params.canonicalMatch || undefined,
    verdict: params.verdict || undefined,
    crawled_as: params.crawledAs || undefined,
    property_site_url: params.propertySiteUrl || undefined,
    group: params.group || undefined,
    monitor: params.monitor || undefined,
    page: params.page || undefined,
    page_size: params.pageSize || undefined,
    crawl_bucket: params.crawlBucket || undefined,
    changes: params.changes ? "1" : undefined,
    change_kind: params.changeKind || undefined,
  };
}

function inspectionExportSearch(params: {
  indexingState?: string;
  coverageState?: string;
  canonicalMatch?: string;
  verdict?: string;
  crawledAs?: string;
  propertySiteUrl?: string;
  group?: string;
  monitor?: string;
  format?: "csv" | "xlsx";
  raw?: boolean;
  crawlBucket?: string;
  changes?: boolean;
  changeKind?: string;
}) {
  const search = new URLSearchParams();
  const query = inspectionResultParams(params);
  (Object.keys(query) as (keyof typeof query)[]).forEach((key) => {
    const value = query[key];
    if (value) {
      // page and page_size are numbers. URLSearchParams.set only accepts strings.
      search.set(key, String(value));
    }
  });
  if (params.crawlBucket) {
    search.set("crawl_bucket", params.crawlBucket);
  }
  if (params.changes) {
    search.set("changes", "1");
  }
  if (params.changeKind) {
    search.set("change_kind", params.changeKind);
  }
  if (params.raw) {
    search.set("raw", "1");
  }
  if (params.format) {
    search.set("format", params.format);
  }
  return search;
}

export default new ApiClient();
